import FileUpload, { FileUploadError, MultipleFilesUploadError } from 'components/CustomUpload/FileUpload';
import { Caption } from 'components/Typography/Typography';
import React, { useState } from 'react';
import styles from 'assets/jss/material-dashboard-pro-react/views/contactUs/uploadFilesStyle';
import { makeStyles } from '@material-ui/core';
import ErrorBox from 'components/ErrorBox/ErrorBox';
import { translate } from 'components/Translation';
import UploadedFile from 'components/UploadedFile/UploadedFile';
import { PROOF_FILES } from 'views/BillingPaymentTrack/commons/constants';

// @ts-ignore
const useStyles = makeStyles(styles);

const MAX_NUMBER_OF_FILES = 5;

interface IFilesProps {
	formData: FormData;
	setFormData: React.Dispatch<React.SetStateAction<FormData>>;
	margins?: boolean;
	title?: JSX.Element;
	subtitle?: JSX.Element;
	maxNumberOfFiles?: number;
	text?: string;
	subtext?: string;
	allowedExtensionsText?: string;
	errorsSubtext?: string;
	acceptedExtensions?: string[];
}

const UploadFiles = ({
	formData,
	setFormData,
	margins = true,
	title,
	subtitle,
	maxNumberOfFiles = MAX_NUMBER_OF_FILES,
	text = translate('billing.payment.track.upload.select.max.five'),
	subtext = translate('billing.payment.track.upload.drag.or.drop'),
	allowedExtensionsText = translate('billing.payment.track.upload.allowed.formats'),
	errorsSubtext = 'billing.payment.track.upload.error',
	acceptedExtensions = [
		'application/pdf',
		'application/msword',
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		'pplication/vnd.ms-excel',
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		'text/csv',
		'text/plain',
		'application/rtf',
		'text/html',
		'application/zip',
		'image/jpeg',
		'image/png',
		'image/gif',
	],
}: IFilesProps) => {
	const [multipleFilesErrors, setMultipleFilesErrors] = useState<Array<MultipleFilesUploadError>>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const uploadDisabled = Array.from(formData.entries()).length >= maxNumberOfFiles;
	const files = Array.from(formData.keys() || []);
	const classes = useStyles({ margins: margins });

	const removeEntry = (entryKey: string | unknown) => {
		setFormData((oldFormData: any) => {
			const newFormData = new FormData();
			let counter = 1;
			for (const pair of oldFormData.entries()) {
				if (pair[0] !== entryKey) {
					newFormData.append(`${PROOF_FILES}${counter}`, pair[1]);
					counter = counter + 1;
				}
			}
			return newFormData;
		});
	};

	const onFileChange = (result: FormData) => {
		setLoading(true);
		const errors: MultipleFilesUploadError[] = [];
		setFormData((oldFormData: any) => {
			const newFormData = new FormData();
			let counter = 1;
			for (const pairOld of oldFormData.entries()) {
				newFormData.append(`${PROOF_FILES}${counter}`, pairOld[1]);
				counter = counter + 1;
			}
			for (const pairResult of result.entries()) {
				if (Array.from(newFormData.entries()).length >= maxNumberOfFiles) {
					errors.push({ fileName: pairResult[0], fileUploadError: FileUploadError.TOO_MANY_FILES });
				} else {
					newFormData.append(`${PROOF_FILES}${counter}`, pairResult[1]);
					counter = counter + 1;
				}
			}
			return newFormData;
		});
		setMultipleFilesErrors((_errors) => _errors.concat(errors));
		setLoading(false);
	};

	return (
		<div className={classes.smallTab}>
			<div className={classes.tab}>
				{title}
				{subtitle}
				<div className={classes.uploadWrapper}>
					<FileUpload
						text={text}
						subtext={subtext}
						onFileChange={onFileChange}
						loadingText={''}
						loading={loading}
						multipleFilesAllowed={true}
						textSize="subtext"
						acceptedExtensions={acceptedExtensions}
						fileMaxSize={10000001}
						insideError={true}
						setMultipleFilesErrors={setMultipleFilesErrors}
						uploadDisabled={uploadDisabled}
					/>
					<div className={classes.allowedFormats}>
						<Caption>{allowedExtensionsText}</Caption>
					</div>
					<div className={classes.uploadProofFiles}>
						{files.map((entry, index) => (
							<UploadedFile
								key={index}
								name={(formData.get(entry) as File).name}
								removeEntry={() => removeEntry(entry)}
							/>
						))}
					</div>
					{multipleFilesErrors.map((error) => (
						<div className={classes.errorBox} key={error.fileName + 'err'}>
							<ErrorBox
								text={translate(
									`${errorsSubtext}.${error.fileUploadError.toLowerCase()}`,
									error.fileName,
									maxNumberOfFiles
								)}
							/>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export default UploadFiles;
